import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, AppBar, Toolbar, Typography, Button } from '@mui/material';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import MainApp from './components/MainApp';
import AuthWrapper from './components/AuthWrapper';
import { createAppTheme } from './styles/theme';

function UserChip() {
  const [email, setEmail] = React.useState(null);

  React.useEffect(() => {
    const load = () =>
      fetchAuthSession()
        .then(s => setEmail(s.tokens?.idToken?.payload?.email || null))
        .catch(() => setEmail(null));

    load();

    return Hub.listen('auth', ({ payload }) => {
      if (['signedIn', 'tokenRefresh'].includes(payload.event)) load();
      if (payload.event === 'signedOut') setEmail(null);
    });
  }, []);

  if (!email) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="inherit">{email}</Typography>
      <Button size="small" color="inherit" onClick={() => signOut()}>Sign Out</Button>
    </Box>
  );
}

function App() {
  // Check if user has a preference stored in localStorage
  const storedMode = localStorage.getItem('themeMode');
  
  // Check if user has a system preference
  const prefersDarkMode = window.matchMedia && 
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // Initialize state with stored preference, system preference, or default to 'light'
  const [mode, setMode] = useState(storedMode || (prefersDarkMode ? 'dark' : 'light'));

  // Update localStorage when mode changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

    // Toggle between light and dark mode
  const toggleMode = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  // Listen for theme toggle events from MainApp
  useEffect(() => {
    const handleToggleTheme = () => {
      toggleMode();
    };

    window.addEventListener('toggleTheme', handleToggleTheme);
    
    return () => {
      window.removeEventListener('toggleTheme', handleToggleTheme);
    };
  }, []);

      const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        bgcolor: theme.palette.mode === 'dark' ? '#121212' : '#f5f5f5'
      }}>
                                                <AppBar position="static" color="default" elevation={1} sx={{ minHeight: '48px' }}>
          <Toolbar variant="dense" sx={{ minHeight: '48px !important', px: 2 }}>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1,
                color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                fontSize: '1.1rem', // Slightly smaller title
                fontWeight: 600
              }}
            >
              Pocket<span style={{ color: '#4db6ac' }}>PM</span>
            </Typography>
                                    <Box sx={{ flexGrow: 1 }} />
            <UserChip />
          </Toolbar>
        </AppBar>
        
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, px: { xs: 1, sm: 2, md: 3 }, flexGrow: 1 }}>
          <AuthWrapper>
            <MainApp />
          </AuthWrapper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function AppWithRouter() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <App />
    </Router>
  );
}

export default AppWithRouter;